<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200 tw-pt-4">
      <base-content class="tw-pt-0 tw-flex tw-items-center tw-justify-between">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Order Preview</h1>
        <div class="tw-flex tw-space-x-4">
          <base-button type="primary" @click="viewHistoryComments">View History Comments</base-button>
        </div>
      </base-content>
    </div>
    <div class="tw-overflow-auto tw-rounded-lg tw-p-4"></div>
    <base-content>
      <div class="tw-max-w-10xl">
        <div class="tw-border tw-bg-white tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-6" role="alert">
          <strong class="tw-font-bold">WARNING!</strong>
          <span class="tw-block sm:tw-inline">
            Please review ALL Patient information and notes before submitting this order to the Pharmacy for filling. If there is a discrepancy, please create a
            <a href="#" class="tw-font-bold tw-underline" @click.prevent="createClarification">Prescriber Clarification</a> to resolve the issue before submission.</span
          >
        </div>
        <div class="tw-grid tw-grid-cols-2  tw-gap-3 tw-mb-3">
          <base-card>
            <patient-prescription
              :prescription="patientData.prescription"
              :allRxItems="allRxItems"
              :todoLabel="activityData && activityData.type ? activityData.type.label : ''"
              :todoItem="activityData"
              ref="patientPrescriptionComponent"
            />
          </base-card>

          <base-card>
            <patient-information :patient-info="patientData.info" :has-address-mismatch="isMailingAddressMismatch" />
          </base-card>
        </div>
        <div class="tw-grid tw-grid-cols-2 tw-gap-3">
          <base-card>
            <activity-submission
              :businessId="businessId"
              :todoItem="activityData"
              :orderWithDetails="orderWithDetails"
              :isLoading="isLoading"
              @history="history"
              @viewSubmittedOrder="viewSubmittedOrder"
              :needs-details-check="needsDetailsCheck"
            />
          </base-card>

          <base-card>
            <pharmacy-selection
              :pharmacy-data="pharmacyData"
              :activity-data="activityData"
              :prescription-data="patientData.prescription"
              :isLoading="isLoading"
              :prescriber-options="prescribers"
              :default-prescriber-id="defaultPrescriberId"
              :supervisingPrescriberName="supervisingPrescriberName"
              :isPrescriberLoading="isPrescriberLoading"
              @submit-order="handleSubmitOrder"
              @create-clarification="createClarification"
              @back-to-profile="backToProfile"
              :title="errorTitle"
              :message="errorMessage"
              :error="error"
              :needs-details-check="needsDetailsCheck"
              :maxCommentLength="maxCommentLength"
              :isPrescriberNpiMissing="isPrescriberNpiMissing"
            />
          </base-card>
        </div>
      </div>
      <view-details ref="historyModal" :businessId="businessId" />
      <NewTodoItem ref="newTodoModal" :businessId="businessId" />
      <clarification ref="clarificationSlideover" :businessId="businessId" />
      <last-submitted-order-modal ref="viewSubmittedOrderModal" :orders="filterOrderWithDetails()" :isLoading="isLoading" />
    </base-content>
  </div>
</template>

<script>
import PatientPrescription from "@/views/business/order/components/BusinessOrderPrescription.vue";
import PatientInformation from "@/views/business/order/components/BusinessMemberInformation.vue";
import ActivitySubmission from "@/views/business/order/components/ActivitySubmission.vue";
import PharmacySelection from "@/views/business/order/components/PharmacySelection.vue";
import ViewDetails from "@/views/business/todo/components/ViewDetails";
import NewTodoItem from "@/views/business/todo/components/NewTodoItem";
import Clarification from "@/views/business/todo/components/Clarification";
import LastSubmittedOrderModal from "@/views/business/order/components/LastSubmittedOrderModal.vue";
import { orderBy, groupBy } from "lodash";

import { mapGetters } from "vuex";

export default {
  name: "BusinessOrderView",
  components: {
    PatientPrescription,
    PatientInformation,
    ActivitySubmission,
    PharmacySelection,
    ViewDetails,
    NewTodoItem,
    Clarification,
    LastSubmittedOrderModal,
  },
  props: {
    businessId: String,
    orderId: String,
  },
  data() {
    return {
      isLoading: true,
      patientData: {
        prescription: null,
        info: null,
      },
      activityData: null,
      orderWithDetails: null,
      pharmacyData: {
        selectedPharmacy: null,
        expediteShipment: false,
        comment: "",
        availablePharmacies: [],
      },
      errorTitle: "",
      errorMessage: "",
      error: null,
      prescribers: [],
      allRxItems: [],
      defaultPrescriberId: null,
      supervisingPrescriberName: null,
      isPrescriberLoading: false,
      medications: [],
      rxItems: [],
      typeDefineRxItems: [],
      typeDefineMedicationIds: null,
      needsDetailsCheck: false,
      isPrescriberNpiMissing: false,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["isLocationFiltered", "isBusinessAdministrator"]),
    isMailingAddressMismatch() {
      if (!this.patientData.info || !this.patientData.info.mailingAddress || !this.orderWithDetails || this.orderWithDetails.length <= 0) {
        return false;
      }
      var patientMailing = this.normalizeAddress(this.patientData.info.mailingAddress);
      const latestOrder = this.orderWithDetails && this.orderWithDetails.length > 0 ? this.orderWithDetails[0] : null;
      const latestShipping = latestOrder ? latestOrder.shippingAddress : "";
      return patientMailing !== latestShipping;
    },
    maxCommentLength: function() {
      var name = this.getFirstRxItemName();
      var nameLen = name.length + 1; // include space after name
      return 120 - nameLen;
    },
  },
  watch: {
    activityData(newVal) {
      if (newVal && newVal.businessLocationId) {
        this.fetchPrescribers();
      } else {
        this.prescribers = [];
        this.defaultPrescriberId = null;
      }
    },
  },
  async created() {
    await this.getRxItems();
    await this.fetchOrderDetails();
  },
  mounted() {
    this.EventBus.$on("activitiesUpdated", () => {
      window.location.reload();
    });
  },
  methods: {
    async fetchOrderDetails() {
      this.isLoading = true;
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/order/${this.orderId}/details`);
        var data = response.data.orderItems;
        this.patientData.prescription = data.businessPrescription;
        this.patientData.info = data.businessPrescription.user;
        data.todoItem.numComments = response.data.numComments;
        this.activityData = data.todoItem;
        this.pharmacyData.availablePharmacies = response.data.pharmacyOptions;
        this.orderWithDetails = data.orderWithDetails;
        this.typeDefineRxItems = data.rxItems || [];
        this.typeDefineMedicationIds = this.activityData.type.medicationIds || null;
        if (this.activityData && this.activityData.numComments > 0) {
          this.needsDetailsCheck = true;
        }
      } catch (error) {
        this.errorTitle = `something went wrong `;
        this.errorMessage = `Failed to load order details. Please try again.`;
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPrescribers() {
      this.isPrescriberLoading = true;
      try {
        // Fetch locations instead of users
        const resLocations = await this.$axios.get(`/businesses/${this.businessId}/locations/all`);
        const allLocations = resLocations.data || [];

        let supervisingPrescriberId = null;
        let supervisingPrescriberName = null;

        // Prepare the prescriber dropdown: all locations with valid prescriberName
        this.prescribers = allLocations
          .sort((a, b) => a.prescriberName && a.prescriberName.localeCompare(b.prescriberName))
          .map((loc) => ({
            value: loc.businessLocationId,
            text: loc.prescriberName,
          }));

        if (this.activityData && this.activityData.businessLocationId) {
          // Priority 1: patientData.prescription.supervisingPrescriberId
          if (this.patientData && this.patientData.prescription && this.patientData.prescription.supervisingPrescriberId) {
            supervisingPrescriberId = this.patientData.prescription.supervisingPrescriberId;
            const location1 = allLocations.find((loc) => loc.businessLocationId === supervisingPrescriberId);
            supervisingPrescriberName = location1 && location1.prescriberName ? location1.prescriberName : null;
          } else {
            // Find the activity location by businessLocationId
            const activityLocation = allLocations.find((loc) => loc.businessLocationId === this.activityData.businessLocationId);

            // Priority 2: Check activity location's supervisingPrescriberId (must be non-null)
            if (activityLocation && activityLocation.supervisingPrescriberId && activityLocation.supervisingPrescriberId.trim() !== "") {
              supervisingPrescriberId = activityLocation.supervisingPrescriberId;
              const location2 = allLocations.find((loc) => loc.businessLocationId === supervisingPrescriberId);
              supervisingPrescriberName = location2 && location2.prescriberName ? location2.prescriberName : null;
            }
            // Priority 3: Use activity location if it has a prescriberName
            else if (activityLocation && activityLocation.prescriberName && activityLocation.prescriberName.trim() !== "") {
              supervisingPrescriberId = activityLocation.businessLocationId;
              supervisingPrescriberName = activityLocation.prescriberName;
            } else {
              // No valid prescriber found
              supervisingPrescriberId = null;
              supervisingPrescriberName = null;
            }
          }

          // Update default values for UI binding
          this.defaultPrescriberId = this.prescribers.some((p) => p.value === supervisingPrescriberId) ? supervisingPrescriberId : null;
          this.supervisingPrescriberName = supervisingPrescriberName;

          // Check if default prescriber has missing/empty/null NPI
          if (this.defaultPrescriberId) {
            const matchedLoc = allLocations.find((loc) => loc.businessLocationId === this.defaultPrescriberId);
            this.isPrescriberNpiMissing = !matchedLoc || !matchedLoc.npi || matchedLoc.npi.trim() === "";
          } else {
            this.isPrescriberNpiMissing = false;
          }
        } else {
          // Fallback if no activityData or locationId
          this.prescribers = [];
          this.defaultPrescriberId = null;
          this.supervisingPrescriberName = null;
          this.isPrescriberNpiMissing = false;
        }
      } catch (e) {
        console.error("fetchPrescribers error:", e);
        this.prescribers = [];
        this.defaultPrescriberId = null;
        this.supervisingPrescriberName = null;
        this.isPrescriberNpiMissing = false;
      } finally {
        this.isPrescriberLoading = false;
      }
    },
    viewHistoryComments() {
      if (this.patientData.info && this.patientData.info.id) {
        const memberId = this.patientData.info.id;
        this.$router.push({
          path: `/businesses/${this.businessId}/members/${memberId}/history`,
          query: { comment: true },
        });
      } else {
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Patient information not available to view history.",
          type: "error",
        });
        console.warn("Cannot navigate to history: Patient info or member ID is missing.");
      }
    },
    async handleSubmitOrder(pharmacySelectionDetails) {
      try {
        this.isLoading = true;
        const patientPrescriptionComponent = this.$refs.patientPrescriptionComponent;

        // Get the current medication and supplies from child data
        const orderMedications = patientPrescriptionComponent.medications || [];
        const orderSupplies = patientPrescriptionComponent.supplies || [];

        // check if supplies set on activity have length must be greater than 0 before submitting the order
        if (this.typeDefineRxItems.length === 0) {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Validation Error",
            message: "This activity type must have at least one supply item added before submitting the order.",
            error: "This activity type must have at least one supply item added before submitting the order.",
          });
          this.isLoading = false;
          return;
        }

        //Check orderMedications length must be greater than 0 before submitting the order
        if (orderMedications.length === 0) {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Validation Error",
            message: "The Rx must have at least one medication added before submitting the order.",
            error: "The Rx must have at least one medication added before submitting the order.",
          });
          this.isLoading = false;
          return;
        }

        //check if OrderSupplies have ApiProductId or Rpc2bProductId mapped before submitting the order based on selected pharmacy
        const selectedPharmacy = pharmacySelectionDetails.selectedPharmacyId;
        const unmappedSupplies = orderSupplies.filter((supply) => {
          if (selectedPharmacy === "Everwell" && supply.name.value !== "No Supplies Needed" && (!supply.apiProductId || supply.apiProductId.trim() === "")) {
            return true;
          }
          if (selectedPharmacy === "RPC2B" && supply.name.value !== "No Supplies Needed" && (!supply.rpc2bProductId || supply.rpc2bProductId.trim() === "")) {
            return true;
          }
          return false;
        });

        if (unmappedSupplies.length > 0) {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Validation Error",
            message: "One or more supply items does not have product Id mapped to the selected pharmacy. Please review the supply items before submitting the order.",
            error: "One or more supply items does not have product Id mapped to the selected pharmacy.",
          });
          this.isLoading = false;
          return;
        }

        // Dynamic comment logic
        var clinicalJustification = this.getFirstRxItemName();
        var userComment = pharmacySelectionDetails.comment || "";
        var comment = "";

        var lifefileBasedPharmacies = ["Innovation", "Lifefile", "OmniScript"];

        if (lifefileBasedPharmacies.includes(selectedPharmacy) && pharmacySelectionDetails.expediteShipment) {
          comment = comment + " This is expedite shipment.";
        }

        if (clinicalJustification) {
          comment += " " + clinicalJustification;
        }

        if (userComment) {
          comment += " " + userComment;
        }

        comment = comment.trim();
        if (comment.length > 120) {
          comment = comment.substring(0, 120);
        }
        const payload = {
          orderId: this.orderId,
          businessPrescriptionId: this.patientData.prescription.id,
          selectedPharmacyId: selectedPharmacy,
          selectedPrescriberId: pharmacySelectionDetails.selectedPrescriberId,
          comment: comment,
          clinicalJustification: clinicalJustification,
          expediteShipment: pharmacySelectionDetails.expediteShipment,
          medications: orderMedications.map((med) => med.businessItemCategory),
          rxItems: orderSupplies.map((rx) => rx),
        };

        const response = await this.$axios.post(`/businesses/${this.businessId}/order`, payload);
        // Handle successful response
        this.$emit("order-submitted", response.data);
        if (!lifefileBasedPharmacies.includes(selectedPharmacy)) {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            type: "success",
            title: "Order In Progress",
            message: "Your pharmacy order is in progress and will be confirmed shortly.",
          });
        } else {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            type: "success",
            title: "Order Submitted",
            message: "Your order has been successfully submitted to the pharmacy.",
          });
        }

        // Grab patient member id and business id for the URL
        const memberId = this.patientData.info.id;
        // Use Vue Router to navigate
        this.$router.push(`/businesses/${this.businessId}/members/${memberId}/business-todo`);
      } catch (error) {
        this.errorTitle = `Error `;
        this.errorMessage = `Failed to submit order . Please try again.`;
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },
    createClarification() {
      if (this.activityData && this.patientData.info && this.patientData.info.id) {
        this.$refs.newTodoModal.show({
          userId: this.patientData.info.id,
          businessPrescriptionId: this.activityData.businessPrescriptionId,
          userBusinessPlanId: this.activityData.userBusinessPlanId,
          businessLocationId: this.activityData.businessLocationId,
          prefillTypeConstant: "PRESCRIBER_CLARIFICATION",
        });
      } else {
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Cannot create clarification: Required patient or activity data is missing.",
          type: "error",
        });
        console.error("Missing data to prefill clarification form:", {
          activityData: this.activityData,
          patientInfo: this.patientData.info,
        });
      }
    },
    backToProfile() {
      if (this.patientData.info && this.patientData.info.id) {
        const memberId = this.patientData.info.id;
        if (this.organization.useTodoSetting && this.isLocationFiltered === false) {
          this.$router.push(`/businesses/${this.businessId}/members/${memberId}/business-todo`);
        }
        if (this.organization.useTodoSetting && this.isLocationFiltered === true) {
          this.$router.push(`/businesses/${this.businessId}/members/${memberId}/location-todo`);
        }
      } else {
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Patient information not available to view history.",
          type: "error",
        });
        console.warn("Cannot navigate to history: Patient info or member ID is missing.");
      }
    },
    history(item) {
      this.$refs.historyModal.show(item);
      this.needsDetailsCheck = false;
    },
    viewSubmittedOrder() {
      this.$refs.viewSubmittedOrderModal.show();
    },
    normalizeAddress(address) {
      if (!address) return "";
      // Join address parts with comma, ignoring null/empty fields
      return [address.line1, address.line2, address.city, address.state, address.zip, "US"]
        .filter((part) => part && part.trim().length > 0)
        .join(", ")
        .trim();
    },
    filterOrderWithDetails() {
      if (!Array.isArray(this.orderWithDetails) || !this.patientData.prescription || !this.patientData.prescription.id) {
        return [];
      }
      return this.orderWithDetails
        .filter((order) => order.todoItem && order.todoItem.businessPrescriptionId === this.patientData.prescription.id)
        .sort((a, b) => new Date(b.insertedDate) - new Date(a.insertedDate))
        .slice(0, 5);
    },
    isOrderAlreadySubmitted() {
      if (!Array.isArray(this.orderWithDetails) || !this.activityData || !this.patientData.prescription) {
        return false;
      }
      return this.orderWithDetails.some(
        (order) => order.todoItem && order.todoItem.id === this.activityData.id && order.todoItem.businessPrescriptionId === this.patientData.prescription.id,
      );
    },
    getFirstRxItemName: function() {
      var groupedItems = this.groupedRxItems();
      if (groupedItems.length > 0 && groupedItems[0].items.length > 0) {
        var first = groupedItems[0].items[0];
        if (first.businessItemCategory && first.businessItemCategory.name && first.businessItemCategory.name.value) {
          return first.businessItemCategory.name.value;
        }
      }
      return "";
    },
    groupedRxItems() {
      const prescription = this.patientData && this.patientData.prescription;
      if (!prescription || !Array.isArray(prescription.rxItems) || prescription.rxItems.length === 0) {
        return [];
      }

      // Filter items where productGroup equals "Clinical Justification" (case-insensitive)
      const filteredItems = prescription.rxItems.filter(
        (item) => item.businessItemCategory && item.businessItemCategory.productGroup && item.businessItemCategory.productGroup.toLowerCase().includes("clinical justification"),
      );

      const grouped = groupBy(filteredItems, (item) => {
        return item.businessItemCategory && item.businessItemCategory.productGroup ? item.businessItemCategory.productGroup : "Other Rx Items";
      });

      return Object.keys(grouped)
        .map((groupName) => {
          const itemsInGroup = grouped[groupName];

          const sortedItemsInGroup = orderBy(itemsInGroup, [(x) => x.sortOrder, (x) => x.businessItemCategory && x.businessItemCategory.name && x.businessItemCategory.name.value]);

          return {
            productGroup: groupName,
            items: sortedItemsInGroup,
          };
        })
        .sort((a, b) => a.productGroup.localeCompare(b.productGroup));
    },
    async getRxItems() {
      try {
        const url = `/businesses/${this.businessId}/item-categories/all`;
        const res = await this.$axios.get(url);
        this.allRxItems = res.data
          .filter((i) => i.type === "rx_item" && i.status === "active" && !i.isDeleted)
          .map((i) => ({
            businessId: i.businessId,
            name: {
              value: i.name || "",
            },
            description: {
              short: i.descriptionShort || "",
              long: i.descriptionLong || "",
            },
            status: {
              value: i.status,
              isActive: i.status === "active",
              isInactive: i.status === "inactive",
              isArchived: false,
            },
            retailPrice: {
              currency: i.retailPriceCurrency || "USD",
              cents: i.retailPriceCents || 0,
            },
            taxable: Boolean(i.taxable),
            type: {
              value: i.type || "rx_item",
              isProduct: false,
              isMedication: false,
              isRxItem: true,
            },
            productGroup: i.productGroup || "",
            version: i.version || 1,
            insertBy: i.insertBy || null,
            insertDate: i.insertDate || null,
            isDeleted: false,
            baseName: i.baseName || null,
            strength: i.strength || null,
            quantity: i.quantity || null,
            quantityUnit: i.quantityUnit || null,
            sig: i.sig || null,
            daysSupply: i.daysSupply || null,
            refills: i.refills || null,
            programFilterType: i.programFilterType || "all",
            programsId: i.programsId || "[]",
            form: i.form || null,
            apiProductId: i.apiProductId || null,
            rpc2bProductId: i.rpc2bProductId || null,
            id: i.businessItemCategoryId || i.id,
          }));
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve Rx Items",
          message: "We were not able to retrieve your Rx Items. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
  },
};
</script>
